class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const updateEvent = FacetFiltersForm.startUpdateEvent(searchParams);
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, updateEvent)
        : FacetFiltersForm.renderSectionFromFetch(url, event, updateEvent);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static startUpdateEvent(searchParams) {
    const { SearchUpdateEvent, CollectionUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent && !CollectionUpdateEvent) return;

    const facetsForm = document.querySelector('facet-filters-form');
    const facetsContainer = document.querySelector('.facets-container');
    const collectionId = facetsContainer?.dataset.collectionId || null;
    const collectionHandle = facetsContainer?.dataset.collectionHandle || '';
    const currentCount = parseInt(document.getElementById('ProductCount')?.dataset.productCount) || 0;
    const urlSearchParams = new URLSearchParams(searchParams);
    const isSearchPage = facetsContainer?.dataset.template === 'search';
    const isCollectionPage = facetsContainer?.dataset.template === 'collection';
    const dispatchTarget = facetsForm || document;

    let deferred;

    if (isSearchPage) {
      deferred = SearchUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new SearchUpdateEvent({
          search: {
            query: urlSearchParams.get('q') || '',
            productFilters: SearchUpdateEvent.parseProductFilters(urlSearchParams),
            sortKey: SearchUpdateEvent.getSortKey(urlSearchParams),
          },
          promise: deferred.promise,
        }),
      );
    } else if (isCollectionPage) {
      // collectionId is null for auto-generated collections (/collections/all,
      // /collections/vendors/<x>, etc.) — the SSE schema accepts that.
      deferred = CollectionUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new CollectionUpdateEvent({
          collection: { id: collectionId, handle: collectionHandle, productsCount: currentCount },
          productFilters: CollectionUpdateEvent.parseProductFilters(urlSearchParams),
          sortKey: CollectionUpdateEvent.getSortKey(urlSearchParams),
          promise: deferred.promise,
        }),
      );
    }

    if (!deferred) return;

    return {
      resolve: (filteredCount) => {
        deferred.resolve({ [isSearchPage ? 'totalCount' : 'productsCount']: filteredCount });
      },
      reject: deferred.reject,
    };
  }

  static renderSectionFromFetch(url, event, updateEvent) {
    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderSection(html, event, updateEvent);
      })
      .catch((error) => {
        console.error(error);
        updateEvent?.reject(error);
      });
  }

  static renderSectionFromCache(filterDataUrl, event, updateEvent) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderSection(html, event, updateEvent);
  }

  static renderSection(html, event, updateEvent) {
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html, updateEvent);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html, updateEvent) {
    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsedHtml.getElementById('ProductCount');
    const count = sourceCount.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.dataset.productCount = sourceCount.dataset.productCount || '';
    container.dataset.totalCount = sourceCount.dataset.totalCount || '';
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));

    updateEvent?.resolve(parseInt(sourceCount.dataset.productCount) || 0);
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (!isTextInput) {
          // Try to return focus to the same checkbox the user just toggled,
          // re-selecting it from the freshly rendered HTML by its id.
          const originatingInputId = event.target.id;
          const matchingInput = originatingInputId
            ? newFacetDetailsElement.querySelector(`#${CSS.escape(originatingInputId)}`)
            : null;

          if (matchingInput) {
            matchingInput.focus();
          } else {
            // Fallback to summary/close button if the checkbox can't be found
            const fallbackSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
              ? `.mobile-facets__close-button`
              : `.facets__summary`;
            const fallbackElement = newFacetDetailsElement.querySelector(fallbackSelector);
            if (fallbackElement) fallbackElement.focus();
          }
        }
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);


/* ============================================================
   VF / Pebble & Wick — Cinematic Facets Enhancements
   Safe layer on top of Dawn facets.js.
   Keeps AJAX filtering, sorting, URL history, price range and active filter removal intact.
   ============================================================ */

(() => {
  if (window.__vfCinematicFacetsLoaded) return;
  window.__vfCinematicFacetsLoaded = true;

  if (typeof FacetFiltersForm === 'undefined') return;

  const root = document.documentElement;

  const originalRenderPage = FacetFiltersForm.renderPage;
  const originalRenderSection = FacetFiltersForm.renderSection;
  const originalRenderProductGridContainer = FacetFiltersForm.renderProductGridContainer;
  const originalRenderProductCount = FacetFiltersForm.renderProductCount;
  const originalRenderFilters = FacetFiltersForm.renderFilters;
  const originalRenderActiveFacets = FacetFiltersForm.renderActiveFacets;
  const originalRenderAdditionalElements = FacetFiltersForm.renderAdditionalElements;
  const originalUpdateURLHash = FacetFiltersForm.updateURLHash;
  const originalToggleActiveFacets = FacetFiltersForm.toggleActiveFacets;
  const originalOnSubmitHandler = FacetFiltersForm.prototype.onSubmitHandler;
  const originalOnActiveFilterClick = FacetFiltersForm.prototype.onActiveFilterClick;
  const originalCreateSearchParams = FacetFiltersForm.prototype.createSearchParams;

  const setFacetState = (state, detail = {}) => {
    const forms = document.querySelectorAll('facet-filters-form');
    const container = document.querySelector('.facets-container');
    const gridContainer = document.getElementById('ProductGridContainer');

    root.dataset.vfFacetsState = state;
    root.classList.toggle('vf-facets-loading', state === 'loading');
    root.classList.toggle('vf-facets-ready', state === 'ready');
    root.classList.toggle('vf-facets-filtering', state === 'filtering');
    root.classList.toggle('vf-facets-error', state === 'error');

    container?.classList.toggle('vf-facets-container-loading', state === 'loading' || state === 'filtering');
    container?.classList.toggle('vf-facets-container-ready', state === 'ready');

    gridContainer?.classList.toggle('vf-product-grid-loading', state === 'loading' || state === 'filtering');
    gridContainer?.classList.toggle('vf-product-grid-ready', state === 'ready');

    forms.forEach((form) => {
      form.dataset.vfFacetsState = state;
      form.classList.toggle('vf-facet-filters-loading', state === 'loading');
      form.classList.toggle('vf-facet-filters-filtering', state === 'filtering');
      form.classList.toggle('vf-facet-filters-ready', state === 'ready');
      form.classList.toggle('vf-facet-filters-error', state === 'error');
    });

    document.dispatchEvent(
      new CustomEvent(`vf:facets-${state}`, {
        bubbles: true,
        detail
      })
    );
  };

  const getFacetMeta = (searchParams = '') => {
    const params = new URLSearchParams(searchParams);
    const activeEntries = Array.from(params.entries()).filter(([key, value]) => value && key !== 'section_id');

    return {
      activeCount: activeEntries.length,
      sortBy: params.get('sort_by') || '',
      query: params.get('q') || '',
      params,
      activeEntries
    };
  };

  const markFacetElements = (rootEl = document) => {
    rootEl.querySelectorAll?.('facet-filters-form').forEach((form) => {
      form.classList.add('vf-facet-filters-form-enhanced');
    });

    rootEl.querySelectorAll?.('.js-filter').forEach((filter, index) => {
      filter.classList.add('vf-facet-filter');
      filter.style.setProperty('--vf-facet-index', index);

      const summary = filter.querySelector('.facets__summary, .mobile-facets__summary');
      summary?.classList.add('vf-facet-filter__summary');

      const items = filter.querySelectorAll('.facets__item, .mobile-facets__item');
      items.forEach((item, itemIndex) => {
        item.classList.add('vf-facet-filter__item');
        item.style.setProperty('--vf-facet-item-index', itemIndex);
      });
    });

    rootEl.querySelectorAll?.('.active-facets facet-remove, facet-remove').forEach((remove, index) => {
      remove.classList.add('vf-facet-remove-enhanced');
      remove.style.setProperty('--vf-active-facet-index', index);
    });

    rootEl.querySelectorAll?.('price-range').forEach((range) => {
      range.classList.add('vf-price-range-enhanced');
    });
  };

  const markGridItems = () => {
    const gridContainer = document.getElementById('ProductGridContainer');
    if (!gridContainer) return;

    gridContainer.querySelectorAll('.grid__item, .collection .product-grid .grid__item').forEach((item, index) => {
      item.classList.add('vf-filtered-grid-item');
      item.style.setProperty('--vf-grid-item-index', index);
    });
  };

  const pulseProductCount = () => {
    ['ProductCount', 'ProductCountDesktop'].forEach((id) => {
      const count = document.getElementById(id);
      if (!count) return;

      count.classList.remove('vf-product-count-pulse');
      void count.offsetWidth;
      count.classList.add('vf-product-count-pulse');

      window.setTimeout(() => {
        count.classList.remove('vf-product-count-pulse');
      }, 720);
    });
  };

  const refreshAfterRender = (html = null) => {
    markFacetElements(document);
    markGridItems();
    pulseProductCount();

    window.VFCinematicTheme?.refresh?.();
    window.VFCinematicAnimations?.refresh?.(document);

    if (typeof initializeScrollAnimationTrigger === 'function') {
      const gridContainer = document.getElementById('ProductGridContainer');
      if (gridContainer) initializeScrollAnimationTrigger(gridContainer);
    }

    document.dispatchEvent(
      new CustomEvent('vf:facets-refreshed', {
        bubbles: true,
        detail: {
          html
        }
      })
    );
  };

  FacetFiltersForm.renderPage = function vfRenderPage(searchParams, event, updateURLHash = true) {
    const meta = getFacetMeta(searchParams);

    setFacetState('loading', {
      searchParams,
      event,
      updateURLHash,
      ...meta
    });

    root.style.setProperty('--vf-active-filter-count', meta.activeCount);

    try {
      return originalRenderPage.call(this, searchParams, event, updateURLHash);
    } catch (error) {
      setFacetState('error', {
        searchParams,
        error
      });
      throw error;
    }
  };

  FacetFiltersForm.renderSection = function vfRenderSection(html, event, updateEvent) {
    const result = originalRenderSection.call(this, html, event, updateEvent);

    refreshAfterRender(html);
    setFacetState('ready', {
      event,
      updateEvent
    });

    return result;
  };

  FacetFiltersForm.renderProductGridContainer = function vfRenderProductGridContainer(html) {
    const gridContainer = document.getElementById('ProductGridContainer');
    gridContainer?.classList.add('vf-product-grid-replacing');

    const result = originalRenderProductGridContainer.call(this, html);

    markGridItems();

    window.requestAnimationFrame(() => {
      document.getElementById('ProductGridContainer')?.classList.remove('vf-product-grid-replacing');
      document.getElementById('ProductGridContainer')?.classList.add('vf-product-grid-replaced');

      window.setTimeout(() => {
        document.getElementById('ProductGridContainer')?.classList.remove('vf-product-grid-replaced');
      }, 900);
    });

    return result;
  };

  FacetFiltersForm.renderProductCount = function vfRenderProductCount(html, updateEvent) {
    const result = originalRenderProductCount.call(this, html, updateEvent);
    const count = parseInt(document.getElementById('ProductCount')?.dataset.productCount, 10) || 0;
    const total = parseInt(document.getElementById('ProductCount')?.dataset.totalCount, 10) || count;

    root.style.setProperty('--vf-product-count', count);
    root.style.setProperty('--vf-product-total-count', total);

    pulseProductCount();

    document.dispatchEvent(
      new CustomEvent('vf:facets-product-count-updated', {
        bubbles: true,
        detail: {
          count,
          total
        }
      })
    );

    return result;
  };

  FacetFiltersForm.renderFilters = function vfRenderFilters(html, event) {
    const result = originalRenderFilters.call(this, html, event);

    markFacetElements(document);

    const targetFilter = event?.target?.closest?.('.js-filter');
    if (targetFilter) {
      targetFilter.classList.add('vf-facet-filter-updated');

      window.setTimeout(() => {
        targetFilter.classList.remove('vf-facet-filter-updated');
      }, 720);
    }

    document.dispatchEvent(
      new CustomEvent('vf:facets-filters-rendered', {
        bubbles: true,
        detail: {
          event,
          targetFilter
        }
      })
    );

    return result;
  };

  FacetFiltersForm.renderActiveFacets = function vfRenderActiveFacets(html) {
    const result = originalRenderActiveFacets.call(this, html);

    const activeFacets = document.querySelectorAll('.active-facets facet-remove, .active-facets__button');
    root.style.setProperty('--vf-active-facet-pill-count', activeFacets.length);

    markFacetElements(document);

    return result;
  };

  FacetFiltersForm.renderAdditionalElements = function vfRenderAdditionalElements(html) {
    const result = originalRenderAdditionalElements.call(this, html);

    document.querySelectorAll('.mobile-facets__open, .mobile-facets__count, .sorting').forEach((element) => {
      element.classList.add('vf-facets-additional-element');
    });

    return result;
  };

  FacetFiltersForm.updateURLHash = function vfUpdateURLHash(searchParams) {
    const previousSearch = window.location.search;
    const result = originalUpdateURLHash.call(this, searchParams);

    document.dispatchEvent(
      new CustomEvent('vf:facets-url-updated', {
        bubbles: true,
        detail: {
          previousSearch,
          searchParams,
          url: window.location.href,
          ...getFacetMeta(searchParams)
        }
      })
    );

    return result;
  };

  FacetFiltersForm.toggleActiveFacets = function vfToggleActiveFacets(disable = true) {
    const result = originalToggleActiveFacets.call(this, disable);

    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('vf-facet-remove-disabled', disable);
    });

    return result;
  };

  FacetFiltersForm.prototype.createSearchParams = function vfCreateSearchParams(form) {
    const result = originalCreateSearchParams.call(this, form);

    this.dataset.vfLastSearchParams = result;
    this.dataset.vfActiveFilterCount = String(getFacetMeta(result).activeCount);

    return result;
  };

  FacetFiltersForm.prototype.onSubmitHandler = function vfOnSubmitHandler(event) {
    this.classList.add('vf-facet-submit-started');
    setFacetState('filtering', {
      event,
      form: event?.target?.closest?.('form') || null
    });

    const result = originalOnSubmitHandler.call(this, event);

    window.setTimeout(() => {
      this.classList.remove('vf-facet-submit-started');
    }, 900);

    return result;
  };

  FacetFiltersForm.prototype.onActiveFilterClick = function vfOnActiveFilterClick(event) {
    const activeFacet = event?.currentTarget?.closest?.('facet-remove, .active-facets__button-wrapper, .active-facets__button');

    activeFacet?.classList.add('vf-active-facet-removing');

    const result = originalOnActiveFilterClick.call(this, event);

    window.setTimeout(() => {
      activeFacet?.classList.remove('vf-active-facet-removing');
    }, 600);

    return result;
  };

  if (typeof PriceRange !== 'undefined' && !PriceRange.prototype.__vfCinematicPatched) {
    PriceRange.prototype.__vfCinematicPatched = true;

    const originalPriceRangeOnRangeChange = PriceRange.prototype.onRangeChange;
    const originalPriceRangeSetMinAndMaxValues = PriceRange.prototype.setMinAndMaxValues;
    const originalPriceRangeAdjustToValidValues = PriceRange.prototype.adjustToValidValues;

    const markPriceRange = (priceRange) => {
      priceRange.classList.add('vf-price-range-enhanced');

      priceRange.querySelectorAll('input').forEach((input, index) => {
        input.classList.add('vf-price-range__input');
        input.style.setProperty('--vf-price-input-index', index);
      });
    };

    PriceRange.prototype.onRangeChange = function vfPriceRangeOnRangeChange(event) {
      markPriceRange(this);
      this.classList.add('vf-price-range-changing');

      const result = originalPriceRangeOnRangeChange.call(this, event);

      this.dispatchEvent(
        new CustomEvent('vf:price-range-changed', {
          bubbles: true,
          detail: {
            input: event.currentTarget,
            value: event.currentTarget?.value
          }
        })
      );

      window.setTimeout(() => {
        this.classList.remove('vf-price-range-changing');
      }, 520);

      return result;
    };

    PriceRange.prototype.setMinAndMaxValues = function vfPriceRangeSetMinAndMaxValues() {
      const result = originalPriceRangeSetMinAndMaxValues.call(this);

      const inputs = this.querySelectorAll('input');
      this.dataset.vfMinValue = inputs[0]?.value || '';
      this.dataset.vfMaxValue = inputs[1]?.value || '';

      markPriceRange(this);

      return result;
    };

    PriceRange.prototype.adjustToValidValues = function vfPriceRangeAdjustToValidValues(input) {
      const before = input?.value;
      const result = originalPriceRangeAdjustToValidValues.call(this, input);

      if (input && before !== input.value) {
        input.classList.add('vf-price-range__input-adjusted');

        window.setTimeout(() => {
          input.classList.remove('vf-price-range__input-adjusted');
        }, 520);
      }

      return result;
    };
  }

  if (typeof FacetRemove !== 'undefined' && !FacetRemove.prototype.__vfCinematicPatched) {
    FacetRemove.prototype.__vfCinematicPatched = true;

    const originalFacetRemoveCloseFilter = FacetRemove.prototype.closeFilter;

    FacetRemove.prototype.closeFilter = function vfFacetRemoveCloseFilter(event) {
      this.classList.add('vf-facet-remove-closing');

      const result = originalFacetRemoveCloseFilter.call(this, event);

      this.dispatchEvent(
        new CustomEvent('vf:facet-remove-clicked', {
          bubbles: true,
          detail: {
            event,
            href: this.querySelector('a')?.href
          }
        })
      );

      window.setTimeout(() => {
        this.classList.remove('vf-facet-remove-closing');
      }, 520);

      return result;
    };
  }

  document.addEventListener('DOMContentLoaded', () => {
    markFacetElements(document);
    markGridItems();
    setFacetState('ready', {
      initial: true,
      ...getFacetMeta(window.location.search.slice(1))
    });
  }, { once: true });

  if (document.readyState !== 'loading') {
    markFacetElements(document);
    markGridItems();
    setFacetState('ready', {
      initial: true,
      ...getFacetMeta(window.location.search.slice(1))
    });
  }

  document.addEventListener('click', (event) => {
    const target = event.target.closest?.('.facets__summary, .mobile-facets__summary, .facets__item, .mobile-facets__item, facet-remove a');
    if (!target) return;

    target.classList.add('vf-facet-clicked');

    window.setTimeout(() => {
      target.classList.remove('vf-facet-clicked');
    }, 420);
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest?.('facet-filters-form input, facet-filters-form select, price-range input');
    if (!input) return;

    input.classList.add('vf-facet-input-changed');

    window.setTimeout(() => {
      input.classList.remove('vf-facet-input-changed');
    }, 520);
  });
})();
/* ============================================================
   VF / Pebble & Wick — Premium Desktop Sort Controller
   Mantém o select original sincronizado para o AJAX do Dawn.
   ============================================================ */

(() => {
  if (window.__vfPremiumDesktopSortLoaded) return;
  window.__vfPremiumDesktopSortLoaded = true;

  const closeSort = (sort, returnFocus = false) => {
    if (!sort) return;

    const trigger = sort.querySelector('[data-vf-sort-trigger]');
    const menu = sort.querySelector('[data-vf-sort-menu]');

    sort.classList.remove('is-open');
    trigger?.setAttribute('aria-expanded', 'false');

    if (menu) menu.hidden = true;
    if (returnFocus) trigger?.focus();
  };

  const closeAllSorts = (except = null) => {
    document.querySelectorAll('[data-vf-sort-select].is-open').forEach((sort) => {
      if (sort !== except) closeSort(sort);
    });
  };

  const closeOtherDesktopFilters = (except = null) => {
    document.querySelectorAll('.facets__disclosure[open]').forEach((details) => {
      if (details !== except) details.removeAttribute('open');
    });
  };

  const syncSort = (sort) => {
    if (!sort) return;

    const select = sort.querySelector('select[name="sort_by"]');
    const current = sort.querySelector('[data-vf-sort-current]');
    const selectedOption = select?.options?.[select.selectedIndex];

    if (current && selectedOption) current.textContent = selectedOption.textContent.trim();

    sort.querySelectorAll('[data-vf-sort-option]').forEach((option) => {
      const isSelected = option.dataset.value === select?.value;
      option.classList.toggle('is-selected', isSelected);
      option.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    });
  };

  const syncAllSorts = () => {
    document.querySelectorAll('[data-vf-sort-select]').forEach(syncSort);
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.('[data-vf-sort-trigger]');

    if (trigger) {
      event.preventDefault();

      const sort = trigger.closest('[data-vf-sort-select]');
      const menu = sort?.querySelector('[data-vf-sort-menu]');
      const shouldOpen = !sort?.classList.contains('is-open');

      closeAllSorts(sort);
      closeOtherDesktopFilters();

      if (shouldOpen && sort && menu) {
        sort.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
        menu.hidden = false;

        window.requestAnimationFrame(() => {
          menu.querySelector('.is-selected, [data-vf-sort-option]')?.focus();
        });
      } else {
        closeSort(sort);
      }

      return;
    }

    const option = event.target.closest?.('[data-vf-sort-option]');

    if (option) {
      event.preventDefault();

      const sort = option.closest('[data-vf-sort-select]');
      const select = sort?.querySelector('select[name="sort_by"]');

      if (!sort || !select) return;

      select.value = option.dataset.value || '';
      syncSort(sort);
      closeSort(sort, true);

      select.dispatchEvent(new Event('input', { bubbles: true }));
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const summary = event.target.closest?.('.facets__summary');

    if (summary) {
      const currentDetails = summary.closest('.facets__disclosure');

      closeAllSorts();
      closeOtherDesktopFilters(currentDetails);
      return;
    }

    if (!event.target.closest?.('[data-vf-sort-select]')) closeAllSorts();
  });

  document.addEventListener('keydown', (event) => {
    const sort = event.target.closest?.('[data-vf-sort-select]');

    if (event.key === 'Escape') {
      closeAllSorts();

      document.querySelectorAll('.facets__disclosure[open]').forEach((details) => {
        details.removeAttribute('open');
      });

      if (sort) sort.querySelector('[data-vf-sort-trigger]')?.focus();
      return;
    }

    if (!sort?.classList.contains('is-open')) return;

    const options = Array.from(sort.querySelectorAll('[data-vf-sort-option]'));
    const currentIndex = options.indexOf(document.activeElement);

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      options[Math.min(currentIndex + 1, options.length - 1)]?.focus();
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      options[Math.max(currentIndex - 1, 0)]?.focus();
    }

    if (event.key === 'Home') {
      event.preventDefault();
      options[0]?.focus();
    }

    if (event.key === 'End') {
      event.preventDefault();
      options[options.length - 1]?.focus();
    }
  });

  document.addEventListener('vf:facets-refreshed', syncAllSorts);
  document.addEventListener('vf:facets-filters-rendered', syncAllSorts);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncAllSorts, { once: true });
  } else {
    syncAllSorts();
  }
})();
